import React, { useState, useRef } from 'react';
import { makeStyles, Switch } from '@fluentui/react-components';
import {
  ImageEdit20Regular,
  Copy20Regular,
  Delete20Regular,
  ArrowUp20Regular,
  ArrowDown20Regular,
  MoreHorizontal20Regular,
  Add20Regular,
  RadioButton20Regular,
} from '@fluentui/react-icons';
import { LikertQuestion as LikertQuestionType } from '../../../../types/forms';

const useStyles = makeStyles({
  questionCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    width: '100%',
    position: 'relative',
  },
  topBar: {
    height: '5px',
    backgroundColor: '#03787c',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    paddingBottom: '10px',
  },
  canvas: {
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
    padding: '0 20px',
  },
  actionButtons: {
    display: 'flex',
    gap: '28px',
    alignItems: 'center',
    justifyContent: 'flex-end',
    cursor: 'pointer',
    paddingTop: '16px',
  },
  actionIcon: {
    fontSize: '20px',
    color: '#616161',
    cursor: 'pointer',
    '&:hover': {
      color: '#03787c',
    },
  },
  questionSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    width: '100%',
  },
  questionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
  questionTitleRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    width: '100%',
  },
  questionNumber: {
    width: '20px',
    height: '20px',
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    color: '#292929',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  questionInput: {
    flex: 1,
    height: '32px',
    backgroundColor: 'white',
    borderRadius: '4px',
    position: 'relative',
    '& input': {
      border: 'none',
      outline: 'none',
      width: '100%',
      height: '100%',
      padding: '6px 40px 6px 10px',
      fontFamily: '"Segoe UI", sans-serif',
      fontSize: '14px',
      color: '#242424',
      backgroundColor: 'transparent',
    },
  },
  inputFocusBorder: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: '#02787c',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
  },
  imageEditIcon: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    color: '#616161',
    '&:hover': {
      color: '#03787c',
    },
  },
  likertGrid: {
    paddingLeft: '28px',
    width: 'calc(100% - 28px)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  gridHeader: {
    display: 'flex',
    alignItems: 'center',
    height: '48px',
    paddingLeft: '180px',
    gap: '0',
    position: 'relative',
  },
  headerLabel: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '12px',
    fontWeight: 400,
    color: '#605e5c',
    textAlign: 'center',
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  addColumnButton: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '20px',
    height: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#03787c',
    fontSize: '20px',
    '&:hover': {
      opacity: 0.8,
    },
  },
  gridRow: {
    display: 'flex',
    alignItems: 'center',
    height: '48px',
    gap: '0',
    position: 'relative',
    '&:nth-child(even)': {
      backgroundColor: '#fafafa',
    },
    '&:nth-child(odd)': {
      backgroundColor: 'white',
    },
  },
  statementInput: {
    height: '32px',
    width: '172px',
    backgroundColor: 'transparent',
    borderRadius: '4px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginLeft: '8px',
    '& input': {
      border: 'none',
      outline: 'none',
      width: '100%',
      height: '100%',
      padding: '0 10px',
      fontFamily: '"Segoe UI", sans-serif',
      fontSize: '14px',
      color: '#242424',
      backgroundColor: 'white',
      borderRadius: '4px',
    },
  },
  statementInputFocusBorder: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: '#02787c',
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
  },
  radioCell: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  radioIcon: {
    fontSize: '20px',
    color: '#a19f9d',
  },
  addButton: {
    paddingLeft: '18px',
    width: 'calc(100% - 18px)',
    marginTop: '16px',
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    color: '#03787c',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 400,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: '#e1e1e1',
  },
  settingsBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 24px 0 16px',
    height: '44px',
  },
  settingsGroup: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  toggleGroup: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    padding: '8px',
  },
  toggleLabel: {
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    color: '#242424',
  },
  moreIcon: {
    cursor: 'pointer',
    color: '#616161',
    '&:hover': {
      color: '#03787c',
    },
  },
});

// Copilot icon component
const CopilotIcon: React.FC<{ className?: string }> = ({ className }) => (
  <img 
    src="/src/assets/office/ribbon/Copilot.svg" 
    alt="Copilot" 
    className={className}
    style={{ width: '20px', height: '20px' }}
  />
);

interface LikertQuestionProps {
  question: LikertQuestionType;
  questionNumber: number;
  onChange: (question: LikertQuestionType) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

export const LikertQuestion: React.FC<LikertQuestionProps> = ({
  question,
  questionNumber,
  onChange,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
}) => {
  const styles = useStyles();
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [focusedStatementIndex, setFocusedStatementIndex] = useState<number | null>(null);
  const [focusedLabelIndex, setFocusedLabelIndex] = useState<number | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const handleTitleChange = (value: string) => {
    onChange({ ...question, title: value });
  };

  const handleStatementChange = (index: number, value: string) => {
    const newStatements = [...question.statements];
    newStatements[index] = value;
    onChange({ ...question, statements: newStatements });
  };

  const handleLabelChange = (index: number, value: string) => {
    if (!question.scale) return;
    
    const newLabels = [...(question.scale.labels || [])];
    // Ensure the labels array has the right length
    const expectedLength = question.scale.max - question.scale.min + 1;
    while (newLabels.length < expectedLength) {
      newLabels.push(`Option ${newLabels.length + 1}`);
    }
    newLabels[index] = value;
    onChange({
      ...question,
      scale: {
        ...question.scale,
        labels: newLabels,
      },
    });
  };

  const handleAddStatement = () => {
    onChange({ 
      ...question, 
      statements: [...question.statements, `Statement ${question.statements.length + 1}`] 
    });
  };

  const handleAddOption = () => {
    if (!question.scale) return;
    
    const newLabels = [...(question.scale.labels || [])];
    newLabels.push(`Option ${question.scale.max - question.scale.min + 2}`);
    onChange({
      ...question,
      scale: {
        ...question.scale,
        max: question.scale.max + 1,
        labels: newLabels,
      },
    });
  };

  const handleToggleRequired = () => {
    onChange({ ...question, required: !question.required });
  };

  return (
    <div className={styles.questionCard}>
      <div className={styles.topBar} />
      
      <div className={styles.contentWrapper}>
        <div className={styles.canvas}>
          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <CopilotIcon className={styles.actionIcon} />
            <Copy20Regular className={styles.actionIcon} onClick={onDuplicate} />
            <Delete20Regular className={styles.actionIcon} onClick={onDelete} />
            <ArrowUp20Regular className={styles.actionIcon} onClick={onMoveUp} />
            <ArrowDown20Regular className={styles.actionIcon} onClick={onMoveDown} />
          </div>

          <div className={styles.questionSection}>
            <div className={styles.questionHeader}>
              {/* Question Title */}
              <div className={styles.questionTitleRow}>
                <div className={styles.questionNumber}>{questionNumber}.</div>
                <div className={styles.questionInput}>
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={question.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    onFocus={() => setIsTitleFocused(true)}
                    onBlur={() => setIsTitleFocused(false)}
                    placeholder="Question"
                  />
                  {isTitleFocused && <div className={styles.inputFocusBorder} />}
                  <ImageEdit20Regular className={styles.imageEditIcon} />
                </div>
              </div>
            </div>

            {/* Likert Grid */}
            {question.scale && (
            <div className={styles.likertGrid}>
              {/* Header */}
              <div className={styles.gridHeader}>
                {Array.from({ length: (question.scale?.max ?? 5) - (question.scale?.min ?? 1) + 1 }, (_, i) => i + (question.scale?.min ?? 1)).map((num, index) => {
                  // Use labels from the scale if provided
                  let label = '';
                  
                  if (question.scale?.labels && question.scale.labels[index]) {
                    // Use the label from the LLM-generated data
                    label = question.scale.labels[index];
                  } else if (index === 0 && question.scale?.minLabel) {
                    // Use minLabel for first option
                    label = question.scale.minLabel;
                  } else if (index === ((question.scale?.max ?? 5) - (question.scale?.min ?? 1)) && question.scale?.maxLabel) {
                    // Use maxLabel for last option
                    label = question.scale.maxLabel;
                  } else {
                    // Fallback to numbered options
                    label = `Option ${num}`;
                  }
                  
                  return (
                    <div key={index} className={styles.headerLabel} style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={label}
                        onChange={(e) => handleLabelChange(index, e.target.value)}
                        onFocus={() => setFocusedLabelIndex(index)}
                        onBlur={() => setFocusedLabelIndex(null)}
                        placeholder={`Option ${num}`}
                        style={{
                          width: '100%',
                          border: 'none',
                          outline: 'none',
                          backgroundColor: 'transparent',
                          textAlign: 'center',
                          fontSize: '12px',
                          fontWeight: 400,
                          color: '#242424',
                          padding: '4px',
                        }}
                      />
                      {focusedLabelIndex === index && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '2px',
                            backgroundColor: '#02787c',
                          }}
                        />
                      )}
                    </div>
                  );
                })}
                <div className={styles.addColumnButton} onClick={handleAddOption}>
                  <Add20Regular />
                </div>
              </div>

              {/* Statement Rows */}
              {question.statements.map((statement, rowIndex) => (
                <div key={rowIndex} className={styles.gridRow}>
                  <div className={styles.statementInput}>
                    <input
                      type="text"
                      value={statement}
                      onChange={(e) => handleStatementChange(rowIndex, e.target.value)}
                      onFocus={() => setFocusedStatementIndex(rowIndex)}
                      onBlur={() => setFocusedStatementIndex(null)}
                      placeholder={`Statement ${rowIndex + 1}`}
                    />
                    {focusedStatementIndex === rowIndex && <div className={styles.statementInputFocusBorder} />}
                  </div>
                  {Array.from({ length: (question.scale?.max ?? 5) - (question.scale?.min ?? 1) + 1 }).map((_, colIndex) => (
                    <div key={colIndex} className={styles.radioCell}>
                      <RadioButton20Regular className={styles.radioIcon} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            )}

            {/* Add Statement Button */}
            <div className={styles.addButton} onClick={handleAddStatement}>
              <Add20Regular style={{ fontSize: '20px' }} />
              <span>Add statement</span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className={styles.divider} />

        {/* Settings Bar */}
        <div className={styles.settingsBar}>
          <div />
          <div className={styles.settingsGroup}>
            <div className={styles.toggleGroup}>
              <Switch
                checked={question.required}
                onChange={handleToggleRequired}
              />
              <span className={styles.toggleLabel}>Required</span>
            </div>
            <MoreHorizontal20Regular className={styles.moreIcon} />
          </div>
        </div>
      </div>
    </div>
  );
};
